import React from 'react';
import { Reveal } from './ui/Reveal';
import { StaggerTitle } from './ui/StaggerTitle';
import { ParallaxImage } from './ui/ParallaxImage';
import { ConsultButton, ContactInline } from './ui/Buttons';
export interface ServiceSectionProps {
  id: string;
  eyebrow?: string;
  title: string;
  body: string;
  image: string;
  imageAlt: string;
  imageWidth?: number;
  imageHeight?: number;
  tone: 'red' | 'dark' | 'cosmic';
  /** Places the image on the right instead of the left */
  reverse?: boolean;
  showContactInline?: boolean;
  footnote?: string;
}
const toneStyles: Record<ServiceSectionProps['tone'], {
  wrapper: string;
  title: string;
  body: string;
  eyebrow: string;
}> = {
  red: {
    wrapper: 'bg-brand-red',
    title: 'title-contour text-white',
    body: 'text-white/90',
    eyebrow: 'text-black/60'
  },
  dark: {
    wrapper: 'bg-brand-ink',
    title: 'glow-red text-brand-red',
    body: 'text-white/75',
    eyebrow: 'text-white/45'
  },
  cosmic: {
    wrapper: 'bg-[#0b0614] star-field',
    title: 'glow-red text-brand-red',
    body: 'text-white/75',
    eyebrow: 'text-white/45'
  }
};
export function ServiceSection({
  id,
  eyebrow,
  title,
  body,
  image,
  imageAlt,
  imageWidth = 1200,
  imageHeight = 900,
  tone,
  reverse = false,
  showContactInline = false,
  footnote
}: ServiceSectionProps) {
  const styles = toneStyles[tone];
  return <section id={id} className={`relative overflow-hidden px-5 py-12 sm:px-8 sm:py-16 lg:py-20 ${styles.wrapper}`} data-id="element-87" data-mp="rydmdach14k5">
      {tone === 'cosmic' && <div aria-hidden="true" className="pointer-events-none absolute inset-0" style={{
      background: 'radial-gradient(60% 55% at 80% 20%, rgba(94,33,168,0.5) 0%, rgba(11,6,20,0) 70%)'
    }} data-id="element-88" data-mp="vxw9mcrrlrq5" />}

      <div className="relative mx-auto grid max-w-6xl items-center gap-12 lg:grid-cols-12 lg:gap-16" data-id="element-89" data-mp="1apwntcqukak2">
        <Reveal className={`lg:col-span-5 ${reverse ? 'lg:order-2 lg:col-start-8' : 'lg:order-1'}`} data-id="element-90" data-mp="1fohrjm5b5cd9">
          <ParallaxImage src={image} alt={imageAlt} width={imageWidth} height={imageHeight} strength={40} className="aspect-[4/3] w-full rounded-3xl shadow-float ring-1 ring-black/25 lg:aspect-[4/5]" data-id="element-91" data-mp="l0hjkgnqwycb" />
        </Reveal>

        <div className={`lg:col-span-6 ${reverse ? 'lg:order-1 lg:col-start-1' : 'lg:order-2 lg:col-start-7'}`} data-id="element-92" data-mp="18q23p2vmy1n6">
          {eyebrow && <Reveal data-id="element-93" data-mp="13t1wgon1usuh">
              <p className={`mb-4 font-serif text-lg italic sm:text-xl ${styles.eyebrow}`} data-id="element-94" data-mp="1inycho13oki4">
                {eyebrow}
              </p>
            </Reveal>}

          <StaggerTitle text={title} className={`font-display text-[11vw] leading-[0.9] sm:text-6xl lg:text-7xl ${styles.title}`} data-id="element-95" data-mp="3kfbfu8nf6kc2" />

          <Reveal delay={0.08} data-id="element-96" data-mp="3ficqe3epb1k7">
            <p className={`mt-7 max-w-xl font-sans text-base leading-[1.75] sm:text-lg ${styles.body}`} data-id="element-97" data-mp="1uypoe9ra2ru9">
              {body}
            </p>
          </Reveal>

          {footnote && <Reveal delay={0.12} data-id="element-98" data-mp="1l960uqsme9t2">
              <p className="mt-4 font-sans text-sm font-semibold tracking-wide text-white" data-id="element-99" data-mp="1l84qkojfhrce">
                {footnote}
              </p>
            </Reveal>}

          <Reveal delay={0.16} data-id="element-100" data-mp="1c7q3bjmwm269">
            <div className="mt-9 flex flex-col items-center gap-6 sm:items-start" data-id="element-101" data-mp="1c6es4yr4ps54">
              {showContactInline && <ContactInline tone={tone === 'red' ? 'light' : 'dark'} data-id="element-102" data-mp="1g78e3ml0gwvd" />}
              <ConsultButton className="w-full max-w-xs sm:w-auto" data-id="element-103" data-mp="12bwkjiildcg1" />
            </div>
          </Reveal>
        </div>
      </div>
    </section>;
}